import React, { useCallback, useEffect, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./LibraryBrowser.scss";

interface LibraryManifestEntry {
  id: string;
  name: string;
  description: string;
  file: string;
  icon?: string;
}

interface LibraryBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}

export const LibraryBrowser: React.FC<LibraryBrowserProps> = ({
  isOpen,
  onClose,
  excalidrawAPI,
}) => {
  const [libraries, setLibraries] = useState<LibraryManifestEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      fetch("/libraries/manifest.json")
        .then((res) => res.json())
        .then((data) => setLibraries(data.libraries || []))
        .catch(() => setLibraries([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const handleAddLibrary = useCallback(
    async (lib: LibraryManifestEntry) => {
      if (!excalidrawAPI) {
        return;
      }
      setLoadingId(lib.id);
      try {
        const res = await fetch(lib.file);
        const data = await res.json();

        if (data.libraryItems) {
          // Merge on server (handles deduplication by item ID)
          await fetch("/api/user-libraries/merge", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ libraryItems: data.libraryItems }),
          });
          // Reload from server and update UI
          const serverData = await fetch("/api/user-libraries", {
            credentials: "include",
          }).then((r) => r.json());
          await excalidrawAPI.updateLibrary({
            libraryItems: serverData.libraryItems || [],
            merge: false, // Full replace with deduped server data
            openLibraryMenu: true,
          });
          setLoadedIds((prev) => new Set([...prev, lib.id]));
        }
      } catch (err) {
        console.error(`Failed to load library ${lib.name}:`, err);
      } finally {
        setLoadingId(null);
      }
    },
    [excalidrawAPI],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="library-browser-overlay" onClick={onClose}>
      <div
        className="library-browser-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="library-browser-header">
          <h2>Architecture Libraries</h2>
          <button className="library-browser-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <p className="library-browser-subtitle">
          Add cloud architecture symbols to your library. You can also import
          custom <code>.excalidrawlib</code> files via the library panel (book
          icon in the toolbar).
        </p>

        <div className="library-browser-content">
          {loading ? (
            <div className="library-browser-loading">Loading libraries...</div>
          ) : libraries.length === 0 ? (
            <div className="library-browser-empty">
              No libraries available. Add <code>.excalidrawlib</code> files to{" "}
              <code>public/libraries/</code> and update{" "}
              <code>manifest.json</code>.
            </div>
          ) : (
            <div className="library-browser-grid">
              {libraries.map((lib) => (
                <div key={lib.id} className="library-browser-card">
                  <div className="library-browser-card-icon">
                    {lib.icon ? (
                      <img src={lib.icon} alt={lib.name} width={48} />
                    ) : (
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                      </svg>
                    )}
                  </div>
                  <div className="library-browser-card-info">
                    <h3>{lib.name}</h3>
                    <p>{lib.description}</p>
                  </div>
                  <button
                    className={`library-browser-add-btn ${loadedIds.has(lib.id) ? "loaded" : ""}`}
                    onClick={() => handleAddLibrary(lib)}
                    disabled={loadingId === lib.id}
                  >
                    {loadingId === lib.id
                      ? "Loading..."
                      : loadedIds.has(lib.id)
                        ? "Added"
                        : "Add to Library"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
